<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcHeader',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
    },
    card: {
      type: Boolean,
      required: false,
    },
    dialogName: {
      type: String,
      required: false,
    },
    title: {
      type: String,
      required: false,
    },
    color: {
      type: String,
      required: false,
    },
    resourceTitle: {
      type: String,
      required: false,
    },
    resourceKey: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);
  const b_2248226175642056_class = computed(() => ({
    'items-spacing': true,
    'w-100': true,
    'me-3': true,
  }));

  const $data = computed(() => ({
    ux: ux.value,
    b_2248226175642056_class: b_2248226175642056_class.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ux' },
      { name: 'b_2248226175642056_class', nodeId: '2248226175642056' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { ux, b_2248226175642056_class },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const borderClass = computed(() => {
    return props.color ? 'uc-border border-top-' + props.color : '';
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.card,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('card');
    }
  );
  watch(
    () => props.dialogName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('dialogName');
    }
  );
  watch(
    () => props.title,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('title');
    }
  );
  watch(
    () => props.color,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('color');
    }
  );
  watch(
    () => props.resourceTitle,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('resourceTitle');
    }
  );
  watch(
    () => props.resourceKey,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('resourceKey');
    }
  );

  const act_5962862420439144_emit_a75x474x9_getActionArgs = async ($event) => {
    return {
      event: 'close',
    };
  };

  const act_5962862420439144_emit_a75x474x9 = new actionTypes.EmitAction({
    actionArgs: act_5962862420439144_emit_a75x474x9_getActionArgs,
  });
  const inst_evh_5962862420439144_click_judk2axtd =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_emit_a75x474x9,
      event: 'click',
      displayName: 'emit',
    });
  const inst_evh_5962862420439144_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5962862420439144_click_judk2axtd],
    event: 'click',
  });

  const actions = {
    inst_evh_5962862420439144_click_judk2axtd,
    inst_evh_5962862420439144_click,
  };

  const evh_5962862420439144_click_judk2axtd = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_click_judk2axtd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_click = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <div
    :class="[
      {
        'modal-header': modal,
        'shadow-sm': modal,
        'card-header': card,
        'bg-gray-100': modal || card,
      },
      borderClass,
    ]"
  >
    <ItpBox alignItems="center" :class="b_2248226175642056_class">
      <ItpBox direction="column" v-if="resourceTitle && resourceKey">
        <ItpText muted :text="resourceTitle" class="mb-0"> </ItpText>
        <ItpText tag="h5" :text="resourceKey" class="mb-0"> </ItpText>
      </ItpBox>
      <ItpText tag="h5" capitalize muted class="mb-0" v-if="dialogName">
        {{ dialogName }}
      </ItpText>
      <span class="m-0 p-0" v-if="dialogName && title"> &middot; </span>
      <ItpText tag="h4" :text="title" class="mb-0" v-if="title"> </ItpText>
      <slot> </slot>
    </ItpBox>
    <button
      type="button"
      aria-label="Close"
      class="close ms-auto me-1"
      v-if="modal"
      @click="evh_5962862420439144_click($event, {})"
    >
      <span aria-hidden="true"> &times; </span>
    </button>
  </div>
</template>
